<script setup lang="ts">
import { computed } from 'vue'
import { isDarkTone, type Tone } from '../tokens'
import type { ScopeNote } from '../types'
import { toneVars } from '../tone-vars'
import ScopeNotes from './ScopeNotes.vue'

/**
 * 面板(卡片)。
 *
 * 只要页面上还散着「自己写一圈边框 + 内边距」的 div,圆角就会开始漂:
 * 12px、10px、8px 各来一个,而它们摆在同一屏里时读起来像不同的东西。
 *
 * `padding="none"` 是给表格用的:表格自己贴边,卡片只负责那圈底与描边。
 */
const props = withDefaults(
  defineProps<{
    title?: string
    subtitle?: string
    notes?: readonly ScopeNote[]
    tone?: Tone
    padding?: 'none' | 'md' | 'lg'
    /** 深色端发光描边 —— 用在需要「这是重点」的大屏面板上 */
    glow?: boolean
  }>(),
  { tone: 'light', padding: 'md', glow: false },
)

const vars = computed(() => toneVars(props.tone))
const hasNotes = computed(() => (props.notes?.length ?? 0) > 0)
</script>

<template>
  <section
    class="panel"
    :class="[{ glow: props.glow, dark: isDarkTone(props.tone) }, `pad-${props.padding}`]"
    :style="vars"
  >
    <header v-if="props.title || props.subtitle || hasNotes || $slots.extra" class="head">
      <div class="head-main">
        <div class="title-row">
          <h3 v-if="props.title" class="title">{{ props.title }}</h3>
          <ScopeNotes v-if="hasNotes && props.notes" :notes="props.notes" :tone="props.tone" />
        </div>
        <p v-if="props.subtitle" class="subtitle">{{ props.subtitle }}</p>
      </div>
      <div class="head-extra">
        <slot name="extra" />
      </div>
    </header>

    <div class="body">
      <slot />
    </div>

    <footer v-if="$slots.footer" class="foot">
      <slot name="footer" />
    </footer>
  </section>
</template>

<style scoped>
.panel {
  display: flex;
  flex-direction: column;
  border-radius: var(--sd-radius-lg);
  background: var(--sd-surface);
  border: 1px solid var(--sd-border);
  box-shadow: var(--sd-shadow-sm);
  transition: transform 180ms ease, box-shadow 180ms ease, border-color 180ms ease;
}

.panel:hover {
  box-shadow: var(--sd-shadow-md);
}

/* 深色端不提位移:深底上的位移会让人觉得整块面板在抖,只提描边亮度就够了 */
.panel.dark:hover {
  transform: none;
  border-color: var(--sd-border-strong);
  box-shadow: var(--sd-shadow-md);
}

.panel.glow {
  box-shadow: var(--sd-shadow-glow);
}

.head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  padding: 14px 20px;
  border-bottom: 1px solid var(--sd-border);
}

.head-main {
  min-width: 0;
}

.title-row {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}

.title {
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  color: var(--sd-text1);
}

.subtitle {
  margin: 4px 0 0;
  font-size: 12.5px;
  color: var(--sd-text3);
}

.head-extra {
  display: flex;
  align-items: center;
  gap: 8px;
  flex: none;
}

.body {
  flex: 1;
  min-width: 0;
}

.pad-none .body {
  padding: 0;
}

.pad-md .body {
  padding: 20px;
}

.pad-lg .body {
  padding: 24px;
}

.foot {
  padding: 12px 20px;
  border-top: 1px solid var(--sd-border);
}
</style>
